/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
*/

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChartBarIcon,
  PlusIcon,
  XMarkIcon,
  SignalIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { monitoringService, ModelMonitor, PerformanceSnapshot } from '../../services/registryApi';
import toast from 'react-hot-toast';

/* ── Status badge ─────────────────────────────────────────────────────── */

const statusColors: Record<string, string> = {
  active: 'bg-green-500/20 text-green-400',
  paused: 'bg-yellow-500/20 text-yellow-400',
  archived: 'bg-gray-700 text-gray-400',
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`px-2 py-0.5 text-xs font-medium rounded ${statusColors[status] || statusColors.archived}`}>
    {status.charAt(0).toUpperCase() + status.slice(1)}
  </span>
);

/* ── Health dot ───────────────────────────────────────────────────────── */

type HealthLevel = 'healthy' | 'warning' | 'critical';

const deriveHealth = (snapshot: PerformanceSnapshot | null | undefined): HealthLevel => {
  if (!snapshot) return 'healthy';
  const errorRate = snapshot.error_rate ?? 0;
  const p99 = snapshot.p99_latency_ms ?? 0;
  if (errorRate >= 10 || p99 >= 1000) return 'critical';
  if (errorRate >= 5 || p99 >= 500) return 'warning';
  return 'healthy';
};

const healthDotColor: Record<HealthLevel, string> = {
  healthy: 'bg-green-400',
  warning: 'bg-yellow-400',
  critical: 'bg-red-400',
};

/* ── Metric helpers ───────────────────────────────────────────────────── */

const fmtLatency = (v: number | null | undefined) =>
  v != null ? `${v.toFixed(1)} ms` : '--';

const fmtPercent = (v: number | null | undefined) =>
  v != null ? `${(v * 100).toFixed(2)}%` : '--';

const fmtCount = (v: number | null | undefined) =>
  v != null ? v.toLocaleString() : '--';

/* ── Window size options ──────────────────────────────────────────────── */

const WINDOW_SIZES = [
  { label: '5 min', value: '5m' },
  { label: '15 min', value: '15m' },
  { label: '1 hour', value: '1h' },
  { label: '6 hours', value: '6h' },
  { label: '1 day', value: '1d' },
];

/* ── Create Monitor Modal ─────────────────────────────────────────────── */

interface CreateMonitorForm {
  model_id: string;
  model_version_id: string;
  name: string;
  endpoint_url: string;
  window_size: string;
  retention_days: number;
}

const emptyForm: CreateMonitorForm = {
  model_id: '',
  model_version_id: '',
  name: '',
  endpoint_url: '',
  window_size: '5m',
  retention_days: 30,
};

const CreateMonitorModal: React.FC<{
  onClose: () => void;
}> = ({ onClose }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<CreateMonitorForm>(emptyForm);

  const mutation = useMutation({
    mutationFn: (data: CreateMonitorForm) =>
      monitoringService.create({
        model_id: data.model_id,
        model_version_id: data.model_version_id,
        name: data.name,
        endpoint_url: data.endpoint_url || undefined,
        window_size: data.window_size,
        retention_days: data.retention_days,
      }),
    onSuccess: () => {
      toast.success('Monitor created');
      queryClient.invalidateQueries({ queryKey: ['monitors'] });
      onClose();
    },
    onError: () => {
      toast.error('Failed to create monitor');
    },
  });

  const handleSubmit = () => {
    if (!form.model_id || !form.model_version_id || !form.name) {
      toast.error('Model ID, Model Version ID, and Name are required');
      return;
    }
    mutation.mutate(form);
  };

  const set = (key: keyof CreateMonitorForm, value: string | number) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-lg mx-4 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">Create Monitor</h2>
          <button onClick={onClose} className="text-gray-600 hover:text-gray-300">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Model ID *</label>
              <input
                type="text"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="model-uuid"
                value={form.model_id}
                onChange={(e) => set('model_id', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Model Version ID *</label>
              <input
                type="text"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="version-uuid"
                value={form.model_version_id}
                onChange={(e) => set('model_version_id', e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Monitor Name *</label>
            <input
              type="text"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="production-fraud-detector"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Endpoint URL</label>
            <input
              type="url"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="https://api.example.com/predict"
              value={form.endpoint_url}
              onChange={(e) => set('endpoint_url', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Window Size</label>
              <select
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                value={form.window_size}
                onChange={(e) => set('window_size', e.target.value)}
              >
                {WINDOW_SIZES.map((ws) => (
                  <option key={ws.value} value={ws.value}>{ws.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Retention (days)</label>
              <input
                type="number"
                min={1}
                max={365}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                value={form.retention_days}
                onChange={(e) => set('retention_days', Number(e.target.value))}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-400 hover:text-white text-sm font-medium rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={mutation.isPending}
            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {mutation.isPending ? 'Creating...' : 'Create Monitor'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Monitor Card ─────────────────────────────────────────────────────── */

const MonitorCard: React.FC<{ monitor: ModelMonitor }> = ({ monitor }) => {
  const { data } = useQuery({
    queryKey: ['monitor-latest', monitor.id],
    queryFn: () => monitoringService.get(monitor.id),
    staleTime: 30_000,
  });

  const latest: PerformanceSnapshot | null = data?.data?.latest_metrics ?? null;
  const health = deriveHealth(latest);

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-xl p-5 hover:border-gray-600 transition-colors">
      {/* Top row: name + status */}
      <div className="flex items-start justify-between mb-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${healthDotColor[health]}`} />
            <h3 className="text-sm font-semibold text-white truncate">{monitor.name}</h3>
          </div>
          <p className="text-xs text-gray-500 mt-1 truncate">
            Model: {monitor.model_id.slice(0, 8)}...
          </p>
        </div>
        <StatusBadge status={monitor.status} />
      </div>

      {/* Metrics summary */}
      {latest ? (
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <p className="text-xs text-gray-500">Requests</p>
            <p className="text-sm font-medium text-white">{fmtCount(latest.request_count)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Error Rate</p>
            <p className={`text-sm font-medium ${(latest.error_rate ?? 0) >= 0.05 ? 'text-red-400' : 'text-white'}`}>
              {fmtPercent(latest.error_rate)}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Avg Latency</p>
            <p className="text-sm font-medium text-white">{fmtLatency(latest.avg_latency_ms)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">P99 Latency</p>
            <p className={`text-sm font-medium ${(latest.p99_latency_ms ?? 0) >= 500 ? 'text-yellow-400' : 'text-white'}`}>
              {fmtLatency(latest.p99_latency_ms)}
            </p>
          </div>
        </div>
      ) : (
        <div className="py-4 text-center text-xs text-gray-500 mb-4">
          No metrics collected yet
        </div>
      )}

      {/* View Details link */}
      <Link
        to={`/registry/monitoring/${monitor.id}`}
        className="block w-full text-center py-2 text-sm font-medium text-orange-400 hover:text-orange-300 border border-gray-700 hover:border-orange-500/50 rounded-lg transition-colors"
      >
        View Details
      </Link>
    </div>
  );
};

/* ── Main Component ───────────────────────────────────────────────────── */

const MonitoringDashboard: React.FC = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['monitors'],
    queryFn: () => monitoringService.list({ limit: 100 }),
  });

  const monitors: ModelMonitor[] = data?.data?.monitors ?? [];

  return (
    <div className="min-h-screen bg-gray-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3">
              <SignalIcon className="w-8 h-8 text-orange-500" />
              <h1 className="text-2xl font-bold text-white">Model Monitoring</h1>
            </div>
            <p className="text-gray-400 mt-1">
              Track performance, latency, and health of deployed models in real time.
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Create Monitor
          </button>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
          </div>
        ) : monitors.length === 0 ? (
          <div className="bg-gray-900 rounded-xl border border-gray-700 p-16 text-center">
            <ChartBarIcon className="mx-auto h-12 w-12 text-gray-600 mb-3" />
            <h3 className="text-sm font-medium text-white mb-1">No monitors yet</h3>
            <p className="text-sm text-gray-400 mb-4">
              Create a monitor to start tracking your deployed model's performance and health.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Create Monitor
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {monitors.map((monitor) => (
              <MonitorCard key={monitor.id} monitor={monitor} />
            ))}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <CreateMonitorModal onClose={() => setShowCreateModal(false)} />
        )}
      </div>
    </div>
  );
};

export default MonitoringDashboard;
